import { useUserContext } from '@/contexts/UserProvider';
import { useAuth } from '@/contexts/AuthProvider';
import { IThreadInteraction } from '@/types';
import { stripSenderPrefix } from '@/utils/humanMessagePrefix';
import { FC, useMemo } from 'react';
import MessageItem from './MessageItem';
import { cn } from '@/lib/utils';
import { humanMessageLabel } from '../lib/humanMessageLabel';

// Internal sync events that should not be shown to SDR agents
const HIDDEN_SYSTEM_EVENTS = new Set([
  'human_connected_sync',
  'human_connection_reset',
  'sdr_joined_in_app',
  'sdr_left_in_app',
  // Slack/Teams pounce — mb-bot-app writes this when an agent clicks Join Chat.
  // Without hiding, the row's `ai` payload (Slack button JSON or the greeting)
  // renders as an SDR bubble. The human-joined banner is already emitted via
  // sendMessageToSocketServer, so this event is redundant in the inbox view.
  'salesperson_joined',
]);

// Human-readable labels for system events. Without this map, MessagesContainer
// renders the raw enum string ("transfer_human") and the system-pill CSS
// uppercases it ("TRANSFER_HUMAN") — visually shouting at the SDR.
const SYSTEM_EVENT_LABELS: Record<string, string> = {
  transfer_human: 'Visitor requested human',
  email_box_shown: 'Asked for email',
  // Agent-triggered on-demand contact-form request (Teams `/collect`, Slack `collect`),
  // distinct from email_box_shown (the AI's own automatic prompt) so the SDR can tell
  // whether the form was requested by them or offered proactively by the bot.
  form_requested: 'Requested contact info',
  email_required_query_failed: 'Email capture failed',
  human_chat_closed: 'Chat closed',
  calendar_shown: 'Calendar shown',
  calendly_shown: 'Calendar shown',
  calendar_booked: 'Meeting booked',
  // agent_joined comes from in-app SDR join (inboxController) and from Slack/Teams pounce
  // (mb-bot-app via humanController). The interaction row's `ai` field has the formatted
  // "<b>name</b>: has joined the chat" string — without a label override the dashboard would
  // show that raw markup as the system pill content.
  agent_joined: 'Agent joined the chat',
  agent_auto_left: 'Agent released (inactive session)',
  // Emitted by mb-server's socket disconnect handler when the visitor closes
  // their tab during an active handoff. Mirrors the "visitor has disconnected"
  // notice Slack/Teams already shows in the agent's thread.
  visitor_disconnected: 'Visitor disconnected from the chat',
};

// Try to parse a stringified email-form payload that the visitor widget submits.
// Backend stores the RAW submission verbatim on the `user` field of the turn's
// pending interaction (before the server has resolved/labeled anything), so
// without parsing we'd render it as a raw JSON "visitor message" bubble. This is
// only a fallback for rows that never get a matching `email_submitted` — the
// normal case renders from that row's server-resolved fields instead (see
// buildContactFormFields), which uses vendor-configured field LABELS rather than
// this payload's raw `customFields` keys (bot custom-field ids like "cf_3k31r1s").
function tryParseEmailPayload(raw?: string): {
  email?: string; firstName?: string; lastName?: string; website?: string; company?: string;
} | null {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === 'object' && (parsed.email || parsed.firstName || parsed.lastName)) {
      return parsed;
    }
  } catch {
    // not valid JSON — leave as plain text
  }
  return null;
}

export interface IContactFormField {
  label: string;
  value: string;
}

// Build the label/value rows for the contact-form card from a server-resolved
// `email_submitted` row (email/first_name/last_name/website land directly on
// this row; custom_field_values comes from the `custom_fields_captured` row
// written just before it in the same submission burst, already keyed by the
// vendor's configured field label).
function buildContactFormFields(
  emailSubmittedRow: IThreadInteraction,
  customFieldsRow?: IThreadInteraction,
): IContactFormField[] {
  const fields: IContactFormField[] = [];
  const name = [emailSubmittedRow.first_name, emailSubmittedRow.last_name].filter(Boolean).join(' ').trim();
  if (name) fields.push({ label: 'Name', value: name });
  if (emailSubmittedRow.email) fields.push({ label: 'Email', value: emailSubmittedRow.email });
  if (emailSubmittedRow.website) fields.push({ label: 'Website', value: emailSubmittedRow.website });
  const customValues = customFieldsRow?.custom_field_values;
  if (customValues && typeof customValues === 'object') {
    Object.entries(customValues).forEach(([label, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        fields.push({ label, value: String(value) });
      }
    });
  }
  return fields;
}

interface IMessagesContainerProps {
  data?: IThreadInteraction[];
  whispers?: Array<{
    id: string;
    author_name: string;
    body: string;
    created_at: string;
  }>;
  isLoading?: boolean;
  visitorName?: string;
}

export type MessageSender = 'visitor' | 'ai' | 'human' | 'system' | 'whisper' | 'contact-form';

export interface IFlattenedMessage {
  id: string | number;
  sender: MessageSender;
  content: string;
  timestamp?: string;
  senderName?: string;
  aiLogo?: string;
  recommendations?: any;
  feedback?: boolean;
  url?: string;
  previousMessage?: string;
  contactFormFields?: IContactFormField[];
}

/**
 * Recover a readable display name from an email-shaped sender string.
 * Old human_message rows persisted before the backend's resolveSdrName fix
 * stored the SDR's email as `sender` because the pre-fix sdrDisplayName never
 * read user_metadata.full_name. We can't update the DB cheaply, so we repair
 * on render: match the current user first (we already know their real name),
 * then fall back to a title-cased local-part. SDRs we don't recognise still
 * get a cleaned-up label instead of "A.ASHARI1302@GMAIL.COM · HUMAN".
 */
function recoverSenderName(
  rawSender: string | null | undefined,
  currentUserEmail: string | undefined,
  currentUserName: string | null | undefined,
): string | undefined {
  if (!rawSender) return undefined;
  if (!rawSender.includes('@')) return rawSender; // already a real name
  if (currentUserEmail && rawSender.toLowerCase() === currentUserEmail.toLowerCase() && currentUserName) {
    return currentUserName;
  }
  // Title-case the local part as a best-effort fallback.
  const local = rawSender.split('@')[0] || '';
  const cleaned = local.replace(/[._-]+/g, ' ').replace(/\d+/g, '').trim();
  if (!cleaned) return rawSender;
  return cleaned
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

const MessagesContainer: FC<IMessagesContainerProps> = ({ data = [], whispers = [], isLoading, visitorName }) => {
  const { askIOTUserDetails } = useUserContext();
  const { user: authUser } = useAuth();

  const currentUserName = useMemo(() => {
    const meta = authUser?.user_metadata as { full_name?: string; first_name?: string; last_name?: string; name?: string } | undefined;
    const fromMeta = meta?.full_name
      || [meta?.first_name, meta?.last_name].filter(Boolean).join(' ').trim()
      || meta?.name;
    if (fromMeta && !fromMeta.includes('@')) return fromMeta.trim();
    const fromProfile = askIOTUserDetails
      ? `${askIOTUserDetails.first_name || ''} ${askIOTUserDetails.last_name || ''}`.trim()
      : '';
    return fromProfile || undefined;
  }, [authUser, askIOTUserDetails]);

  const flattenedMessages = useMemo(() => {
    const messages: IFlattenedMessage[] = [];

    data.forEach((item, index) => {
      // custom_fields_captured never renders on its own — its custom_field_values
      // (vendor-label-keyed) are folded into the contact-form card built below
      // from the email_submitted row that always follows it in the same burst.
      if (item.event === 'custom_fields_captured') return;

      if (item.event === 'email_submitted') {
        // The labeled custom-field values live on the nearest preceding
        // custom_fields_captured row from this same submission (small lookback
        // window — the two writes are always adjacent in the turn sequence).
        const customFieldsRow = data
          .slice(Math.max(0, index - 3), index)
          .reverse()
          .find((r) => r.event === 'custom_fields_captured');
        const contactFormFields = buildContactFormFields(item, customFieldsRow);
        // Defensive fallback for rows written before email/first_name/last_name
        // landed on email_submitted directly — don't render an empty card.
        if (contactFormFields.length === 0) {
          messages.push({
            id: `${item.id}-system-${index}`,
            sender: 'system',
            content: 'Email provided',
            timestamp: item.timestamp,
          });
          return;
        }
        messages.push({
          id: `${item.id}-contact-form-${index}`,
          sender: 'contact-form',
          content: '',
          timestamp: item.timestamp,
          contactFormFields,
        });
        return;
      }

      // Detect a serialized email-form payload anywhere on item.user. This is
      // a fallback only: the normal case is handled above by the email_submitted
      // row that follows it, which carries server-resolved (labeled) fields —
      // skip here whenever that row is coming up so we don't render it twice.
      const inlineEmailPayload = tryParseEmailPayload(item.user || undefined);
      if (inlineEmailPayload) {
        const hasUpcomingEmailSubmitted = data
          .slice(index + 1, index + 6)
          .some((r) => r.event === 'email_submitted');
        if (hasUpcomingEmailSubmitted) return;

        const nameParts = [inlineEmailPayload.firstName, inlineEmailPayload.lastName].filter(Boolean).join(' ').trim();
        const fields: IContactFormField[] = [];
        if (nameParts) fields.push({ label: 'Name', value: nameParts });
        if (inlineEmailPayload.email) fields.push({ label: 'Email', value: inlineEmailPayload.email });
        if (inlineEmailPayload.company) fields.push({ label: 'Company', value: inlineEmailPayload.company });
        if (inlineEmailPayload.website) fields.push({ label: 'Website', value: inlineEmailPayload.website });
        messages.push({
          id: `${item.id}-contact-form-${index}`,
          sender: 'contact-form',
          content: '',
          timestamp: item.timestamp,
          contactFormFields: fields,
        });
        return; // skip the visitor + system push below for this row
      }

      // 1. User/Visitor message
      if (item.user) {
        messages.push({
          id: `${item.id}-user-${index}`,
          sender: 'visitor',
          content: item.user,
          timestamp: item.timestamp,
        });
      }

      // 2. AI or Human message
      if (item.ai || item.is_calendar || item.is_email || item.event) {
        if (item.event === 'human_message') {
          const senderName = recoverSenderName(item.sender, authUser?.email, currentUserName);
          messages.push({
            id: `${item.id}-human-${index}`,
            sender: 'human',
            content: stripSenderPrefix(item.ai || '', senderName),
            timestamp: item.timestamp,
            senderName: humanMessageLabel(senderName, currentUserName),
          });
        } else if (item.event && HIDDEN_SYSTEM_EVENTS.has(item.event)) {
          // Hidden internal events must stop here. Membership in
          // HIDDEN_SYSTEM_EVENTS previously only skipped the system-pill branch
          // below, so the row fell through to the final `item.ai || !item.event`
          // branch and rendered its payload as an *AI agent* bubble instead —
          // the opposite of hiding it. mb-bot-app writes a `salesperson_joined`
          // row carrying the agent's message text on every Teams/Slack reply, so
          // each reply appeared twice in the inbox: once as "NAME · HUMAN" and
          // once as "AI AGENT" (etherfax, 2026-07-31).
        } else if (item.event) {
          // transfer_human rows carry a real, safe-to-render handoff/email-capture
          // message in `ai` (never raw markup, unlike e.g. agent_joined) — show it
          // instead of the generic "Visitor requested human" pill so the SDR sees
          // what the bot actually told the visitor, not just that a handoff fired.
          const content = item.event === 'transfer_human' && item.ai
            ? item.ai
            : SYSTEM_EVENT_LABELS[item.event] || item.ai || item.event;
          messages.push({
            id: `${item.id}-system-${index}`,
            sender: 'system',
            content,
            timestamp: item.timestamp,
          });
        } else if (item.is_calendar && !item.ai) {
          messages.push({
            id: `${item.id}-system-${index}`,
            sender: 'system',
            content: 'Booking calendar displayed to visitor',
            timestamp: item.timestamp,
          });
        } else if (item.is_email && !item.ai) {
          messages.push({
            id: `${item.id}-system-${index}`,
            sender: 'system',
            content: 'Contact info request (email form) displayed to visitor',
            timestamp: item.timestamp,
          });
        } else if (item.ai || !item.event) {
          messages.push({
            id: `${item.id}-ai-${index}`,
            sender: 'ai',
            content: item.ai || '',
            timestamp: item.timestamp,
            aiLogo: item.aiLogo,
            recommendations: item.recommendations,
            feedback: item.feedback,
            url: item.url,
            previousMessage: item.user,
          });
        }
      }
    });

    whispers.forEach((whisper, index) => {
      messages.push({
        id: whisper.id || `whisper-${index}`,
        sender: 'whisper',
        content: whisper.body,
        timestamp: whisper.created_at,
        senderName: whisper.author_name || 'Agent',
      });
    });

    // Visitor and AI from the same interaction share the same DB timestamp (one
    // row per Q&A turn). On ties, sort by chronological role: visitor speaks
    // first, then any whisper/system markers attached to that moment, then the
    // human/AI reply. Without this, alphabetic id sort puts "ai" before "user"
    // and the SDR sees the response above the question that triggered it.
    const senderOrder: Record<MessageSender, number> = {
      visitor: 0,
      whisper: 1,
      system: 2,
      'contact-form': 2,
      human: 3,
      ai: 4,
    };
    return messages.sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      if (aTime !== bTime) return aTime - bTime;
      const aPri = senderOrder[a.sender] ?? 99;
      const bPri = senderOrder[b.sender] ?? 99;
      if (aPri !== bPri) return aPri - bPri;
      return String(a.id).localeCompare(String(b.id));
    });
  }, [data, whispers, authUser?.email, currentUserName]);

  return (
    <div className="flex flex-col min-h-full">
      {flattenedMessages.map((msg, index) => {
        const prevMsg = flattenedMessages[index - 1];
        const nextMsg = flattenedMessages[index + 1];

        const isNamedSender = msg.sender === 'human' || msg.sender === 'whisper';
        const isFirstInGroup = prevMsg?.sender !== msg.sender
          || (isNamedSender && prevMsg?.senderName !== msg.senderName);
        const isLastInGroup = nextMsg?.sender !== msg.sender
          || (isNamedSender && nextMsg?.senderName !== msg.senderName);

        return (
          <div 
            key={msg.id} 
            className={cn(
              "flex flex-col",
              isLastInGroup ? "mb-[18px]" : "mb-1"
            )}
          >
            {msg.sender === 'system' ? (
              // Quieter, non-uppercase pill so a run of status markers (transfer,
              // email submitted, agent joined, agent left) doesn't visually
              // dominate the actual visitor/AI conversation.
              <div className="my-0.5 flex items-center gap-3 py-0.5">
                <div className="h-px flex-1 bg-slate-100" />
                <span className="text-[11.5px] tracking-[0.01em] text-slate-400">{msg.content}</span>
                <div className="h-px flex-1 bg-slate-100" />
              </div>
            ) : msg.sender === 'whisper' ? (
              <div className="my-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 shadow-sm">
                <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
                  Internal note · From {msg.senderName || 'Agent'}
                </div>
                <div className="mt-1 text-[13px] leading-relaxed text-slate-800">{msg.content}</div>
                <div className="mt-2 text-[11px] text-amber-700">Only your team sees this</div>
              </div>
            ) : msg.sender === 'contact-form' ? (
              // The actual submitted form, not a status pill — the SDR asked for
              // this ("show the actual form that was sent") instead of separate
              // "custom_fields_captured" / "Email provided" pills that carried no data.
              <div className="my-3 flex justify-center">
                <div className="w-full max-w-sm rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 shadow-sm">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                    Contact form submitted
                  </div>
                  <dl className="mt-1.5 space-y-1">
                    {(msg.contactFormFields ?? []).map((field) => (
                      <div key={field.label} className="flex gap-2 text-[13px] leading-relaxed">
                        <dt className="text-slate-500 shrink-0">{field.label}:</dt>
                        <dd className="text-slate-800 break-words">{field.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            ) : (
              <MessageItem
                sender={msg.sender}
                content={msg.content}
                senderName={msg.senderName}
                aiLogo={msg.aiLogo}
                recommendations={msg.recommendations}
                feedback={msg.feedback}
                url={msg.url}
                previousMessage={msg.previousMessage}
                isFirstInGroup={isFirstInGroup}
                isLastInGroup={isLastInGroup}
                timestamp={msg.timestamp}
                visitorName={visitorName}
              />
            )}
          </div>
        );
      })}
      {isLoading && (
        <div className="mb-6">
          <MessageItem 
            sender="ai" 
            content="" 
            isLoading 
            isFirstInGroup 
            isLastInGroup 
            visitorName={visitorName}
          />
        </div>
      )}
    </div>
  );
};

export default MessagesContainer;
